
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Send, 
  Package, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Truck, 
  AlertCircle,
  Eye,
  Plus,
  Settings,
  BarChart,
  Building2,
  Phone,
  MapPin
} from 'lucide-react';
import { mockOrders, mockThirdPartyProviders } from '@/api/mockData';
import { useToast } from '@/hooks/use-toast';
import type { Order } from '@/types';

const ThirdPartyManager: React.FC = () => {
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');
  const { toast } = useToast();

  // Filter orders that can be sent to 3PL (not already sent)
  const availableOrders = mockOrders.filter(order => 
    !order.is3PL && 
    ['Chờ xử lý', 'Mới tạo'].includes(order.status)
  );

  // Orders already sent to 3PL
  const sentOrders = mockOrders.filter(order => order.is3PL);
  const pendingOrders = sentOrders.filter(order => order.status === 'Đang luân chuyển');
  const completedOrders = sentOrders.filter(order => order.status === 'Đã giao');

  const handleOrderSelect = (orderId: string, checked: boolean) => {
    if (checked) {
      setSelectedOrders([...selectedOrders, orderId]);
    } else {
      setSelectedOrders(selectedOrders.filter(id => id !== orderId));
    }
  };

  const handleSelectAll = (checked: boolean, orders: Order[]) => {
    if (checked) {
      setSelectedOrders([...selectedOrders, ...orders.map(o => o.id)]);
    } else {
      const orderIds = orders.map(o => o.id);
      setSelectedOrders(selectedOrders.filter(id => !orderIds.includes(id)));
    }
  };

  const sendTo3PL = () => {
    if (!selectedProvider || selectedOrders.length === 0) {
      toast({
        title: "Lỗi",
        description: "Vui lòng chọn nhà cung cấp 3PL và ít nhất một đơn hàng",
        variant: "destructive",
      });
      return;
    }

    const provider = mockThirdPartyProviders.find(p => p.id === selectedProvider);
    
    toast({
      title: "Gửi đơn hàng thành công",
      description: `Đã gửi ${selectedOrders.length} đơn hàng đến ${provider?.name}`,
    });

    // Reset selection
    setSelectedOrders([]);
    setSelectedProvider('');
    setShowSendDialog(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Hoàn thành':
      case 'Đã giao':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'Đang giao':
      case 'Đang luân chuyển':
        return <Truck className="h-4 w-4 text-blue-600" />;
      case 'Chờ xử lý':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <Package className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Hoàn thành':
      case 'Đã giao':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Đang giao':
      case 'Đang luân chuyển':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Chờ xử lý':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Quản lý 3PL</h2>
          <p className="text-muted-foreground mt-1">
            Quản lý đơn hàng gửi qua nhà cung cấp dịch vụ logistics bên thứ 3
          </p>
        </div>
        <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
          <DialogTrigger asChild>
            <Button 
              disabled={selectedOrders.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="h-4 w-4 mr-2" />
              Gửi đơn đã chọn ({selectedOrders.length})
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Gửi đơn hàng đến 3PL</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Bạn đang gửi {selectedOrders.length} đơn hàng đến nhà cung cấp 3PL.
                  Thao tác này không thể hoàn tác.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-3">
                <label className="text-sm font-medium">Chọn nhà cung cấp 3PL:</label>
                {mockThirdPartyProviders
                  .filter(provider => provider.status === 'active')
                  .map((provider) => (
                  <div
                    key={provider.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedProvider === provider.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                    onClick={() => setSelectedProvider(provider.id)}
                  >
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        checked={selectedProvider === provider.id}
                        onChange={() => setSelectedProvider(provider.id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{provider.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {provider.ordersCount} đơn • {provider.successRate}% thành công
                        </div>
                      </div>
                      <Badge 
                        className={provider.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                        }
                      >
                        {provider.status === 'active' ? 'Hoạt động' : 'Tạm dừng'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowSendDialog(false)}>
                  Hủy
                </Button>
                <Button onClick={sendTo3PL}>
                  Xác nhận gửi
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Chờ gửi</p>
                <p className="text-2xl font-bold">{availableOrders.length}</p>
              </div>
              <Package className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Đang vận chuyển</p>
                <p className="text-2xl font-bold">{pendingOrders.length}</p>
              </div>
              <Truck className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Đã giao thành công</p>
                <p className="text-2xl font-bold">{completedOrders.length}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Nhà cung cấp hoạt động</p>
                <p className="text-2xl font-bold">{mockThirdPartyProviders.filter(p => p.status === 'active').length}</p>
              </div>
              <Building2 className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="available" className="space-y-4">
        <TabsList>
          <TabsTrigger value="available">
            Đơn hàng chờ gửi ({availableOrders.length})
          </TabsTrigger>
          <TabsTrigger value="sent">
            Đã gửi 3PL ({sentOrders.length})
          </TabsTrigger>
          <TabsTrigger value="providers">
            Nhà cung cấp ({mockThirdPartyProviders.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="available" className="space-y-4">
          {availableOrders.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Chọn đơn hàng để gửi</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSelectAll(
                      !availableOrders.every(o => selectedOrders.includes(o.id)),
                      availableOrders
                    )}
                  >
                    {availableOrders.every(o => selectedOrders.includes(o.id)) ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {availableOrders.map((order) => (
                  <div
                    key={order.id}
                    className={`p-3 border rounded-lg transition-colors ${
                      selectedOrders.includes(order.id)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={selectedOrders.includes(order.id)}
                        onCheckedChange={(checked) => 
                          handleOrderSelect(order.id, checked as boolean)
                        }
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(order.status)}
                            <span className="font-medium">{order.id}</span>
                            <Badge className={getStatusColor(order.status)}>
                              {order.status}
                            </Badge>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {new Date(order.timestamp).toLocaleString('vi-VN')}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Người gửi:</span> {order.sender}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Người nhận:</span> {order.recipient}
                          </div>
                          <div>
                            <span className="text-muted-foreground">COD:</span> {order.codAmount.toLocaleString()}đ
                          </div>
                          <div>
                            <span className="text-muted-foreground">Cân nặng:</span> {order.weight}g
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {availableOrders.length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Không có đơn hàng nào để gửi đến 3PL</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sent" className="space-y-4">
          {sentOrders.length > 0 ? (
            <div className="space-y-3">
              {sentOrders.map((order) => (
                <Card key={order.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(order.status)}
                        <span className="font-medium">{order.id}</span>
                        <Badge className={getStatusColor(order.status)}>
                          {order.status}
                        </Badge>
                        <Badge variant="outline">
                          3PL: {mockThirdPartyProviders.find(p => p.id === order.thirdPartyId)?.name}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Eye className="h-4 w-4 mr-1" />
                              Chi tiết
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                            <DialogHeader>
                              <DialogTitle>Chi tiết đơn hàng 3PL - {order.id}</DialogTitle>
                            </DialogHeader>
                            <div className="flex-1 overflow-y-auto space-y-6">
                              {/* Order Status and 3PL Info */}
                              <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                                <div className="flex items-center gap-3">
                                  {getStatusIcon(order.status)}
                                  <div>
                                    <div className="font-semibold">{order.id}</div>
                                    <div className="text-sm text-muted-foreground">
                                      Gửi lúc: {new Date(order.timestamp).toLocaleString('vi-VN')}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <Badge className={getStatusColor(order.status)}>
                                    {order.status}
                                  </Badge>
                                  <div className="mt-1">
                                    <Badge variant="secondary">
                                      {mockThirdPartyProviders.find(p => p.id === order.thirdPartyId)?.name}
                                    </Badge>
                                  </div>
                                </div>
                              </div>

                              {/* Sender and Recipient Info */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Card>
                                  <CardHeader className="pb-3">
                                    <CardTitle className="text-base text-blue-600">Thông tin người gửi</CardTitle>
                                  </CardHeader>
                                  <CardContent className="space-y-2 text-sm bg-blue-50 dark:bg-blue-950/20">
                                    <div className="font-medium">{order.sender}</div>
                                    <div className="flex items-center gap-2">
                                      <Phone className="h-3 w-3" />
                                      <a href={`tel:${order.senderPhone}`} className="text-blue-600 hover:underline">
                                        {order.senderPhone}
                                      </a>
                                    </div>
                                  </CardContent>
                                </Card>
                                <Card>
                                  <CardHeader className="pb-3">
                                    <CardTitle className="text-base text-green-600">Thông tin người nhận</CardTitle>
                                  </CardHeader>
                                  <CardContent className="space-y-2 text-sm bg-green-50 dark:bg-green-950/20">
                                    <div className="font-medium">{order.recipient}</div>
                                    <div className="flex items-center gap-2">
                                      <Phone className="h-3 w-3" />
                                      <a href={`tel:${order.recipientPhone}`} className="text-blue-600 hover:underline">
                                        {order.recipientPhone}
                                      </a>
                                    </div>
                                    <div className="flex items-start gap-2">
                                      <MapPin className="h-3 w-3 mt-0.5" />
                                      <span>{order.address}</span>
                                    </div>
                                  </CardContent>
                                </Card>
                              </div>

                              {/* Package Details */}
                              <Card>
                                <CardHeader className="pb-3">
                                  <CardTitle className="text-base text-orange-600">Thông tin gói hàng</CardTitle>
                                </CardHeader>
                                <CardContent className="bg-orange-50 dark:bg-orange-950/20">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                      <span className="text-muted-foreground">Cân nặng:</span>
                                      <div className="font-medium">{order.weight}g</div>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Dịch vụ:</span>
                                      <div className="font-medium">{order.service}</div>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Kích thước:</span>
                                      <div className="font-medium">{order.dimensions || 'N/A'}</div>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">COD:</span>
                                      <div className="font-bold text-primary">
                                        {order.codAmount.toLocaleString()}đ
                                      </div>
                                    </div>
                                  </div>
                                  {order.products && order.products.length > 0 && (
                                    <div className="mt-4">
                                      <div className="text-muted-foreground mb-2">Sản phẩm:</div>
                                      <div className="space-y-1">
                                        {order.products.map((product, idx) => (
                                          <div key={idx} className="flex justify-between items-center p-2 bg-background rounded border">
                                            <span>{product.name} x{product.quantity}</span>
                                            <span className="font-medium">{(product.value * product.quantity).toLocaleString()}đ</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </CardContent>
                              </Card>

                              {/* 3PL Tracking Info */}
                              {order.trackingHistory && order.trackingHistory.length > 0 && (
                                <Card>
                                  <CardHeader className="pb-3">
                                    <CardTitle className="text-base text-purple-600">Lịch sử theo dõi 3PL</CardTitle>
                                  </CardHeader>
                                  <CardContent className="bg-purple-50 dark:bg-purple-950/20">
                                    <div className="space-y-3">
                                      {order.trackingHistory.map((event, idx) => (
                                        <div key={idx} className="flex gap-3">
                                          <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                                          <div className="flex-1 text-sm">
                                            <div className="font-medium">{event.status}</div>
                                            <div className="text-muted-foreground">
                                              {new Date(event.time).toLocaleString('vi-VN')} • {event.location}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </CardContent>
                                </Card>
                              )}
                            </div>
                            <div className="flex-shrink-0 pt-4 border-t">
                              <div className="flex gap-2">
                                <Button variant="outline" className="flex-1">
                                  Xem trên 3PL
                                </Button>
                                <Button variant="destructive" className="flex-1">
                                  Thu hồi đơn
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => toast({
                            title: "Thu hồi đơn hàng",
                            description: `Đã gửi yêu cầu thu hồi đơn ${order.id} từ 3PL`,
                          })}
                        >
                          Thu hồi
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => toast({
                            title: "Hủy đơn hàng",
                            description: `Đã hủy đơn hàng ${order.id}`,
                          })}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Hủy
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Người gửi:</span><br />
                        <a href={`tel:${order.senderPhone}`} className="text-blue-600 hover:underline">
                          {order.sender} - {order.senderPhone}
                        </a>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Người nhận:</span><br />
                        <a href={`tel:${order.recipientPhone}`} className="text-blue-600 hover:underline">
                          {order.recipient} - {order.recipientPhone}
                        </a>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Thông tin:</span><br />
                        COD: {order.codAmount.toLocaleString()}đ • {order.weight}g
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <Send className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Chưa có đơn hàng nào được gửi đến 3PL</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="providers" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Danh sách nhà cung cấp 3PL</CardTitle>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Thêm nhà cung cấp
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mockThirdPartyProviders.map((provider) => (
                  <Card
                    key={provider.id}
                    className={`border-2 transition-all ${
                      provider.status === 'active'
                        ? 'border-green-200 bg-green-50/50' 
                        : 'border-gray-200 bg-gray-50/50'
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${
                            provider.status === 'active' ? 'bg-green-100' : 'bg-gray-100'
                          }`}>
                            <Building2 className={`h-5 w-5 ${
                              provider.status === 'active' ? 'text-green-600' : 'text-gray-500'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{provider.name}</h3>
                            <p className="text-xs text-muted-foreground">
                              {provider.apiUrl}
                            </p>
                          </div>
                        </div>
                        <Badge 
                          className={provider.status === 'active'
                            ? "bg-green-100 text-green-800 border-green-200" 
                            : "bg-gray-100 text-gray-600"
                          }
                        >
                          {provider.status === 'active' ? 'Hoạt động' : 'Tạm dừng'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tổng đơn:</span>
                          <span className="font-medium">{provider.ordersCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tỷ lệ thành công:</span>
                          <span className="font-medium text-green-600">{provider.successRate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">API Key:</span>
                          <span className="font-medium font-mono text-xs">{provider.apiKey.substring(0, 8)}...</span>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2 mt-4 pt-3 border-t">
                        <Button size="sm" variant="outline" className="flex-1">
                          <Settings className="h-4 w-4 mr-1" />
                          Cấu hình
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1">
                          <BarChart className="h-4 w-4 mr-1" />
                          Thống kê
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ThirdPartyManager;
